// FeeLateConfigSetup.tsx
import React, { useState, useRef, useEffect } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Chip } from 'primereact/chip';
import { useClassesStore } from '../../stateManagers/store/useClassesStore';
import { useSchoolDetailsStore } from '../../stateManagers/newStores/useSchoolDetailsStore';
import apiClient from '../../stateManagers/services/axiosInterceptor';

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const Ico = ({
  d, size = 16, style = {}, fill = false, viewBox = '0 0 24 24',
}: {
  d: string | string[]; size?: number; style?: React.CSSProperties; fill?: boolean; viewBox?: string;
}) => (
  <svg
    width={size} height={size} viewBox={viewBox}
    fill={fill ? 'currentColor' : 'none'}
    stroke={fill ? 'none' : 'currentColor'}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IcoCalendar = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d={['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z']} />;
const IcoGradCap = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d={['M22 10v6M2 10l10-5 10 5-10 5z', 'M6 12v5c3 3 9 3 12 0v-5']} />;
const IcoCheck = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M20 6L9 17l-5-5" />;
const IcoX = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M18 6L6 18M6 6l12 12" />;
const IcoTrash = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />;
const IcoPencil = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />;
const IcoInfo = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 8v4M12 16h.01" />;
const IcoWarn = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" />;
const IcoClone = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d={['M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2', 'M16 8h2a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-2']} />;
const IcoRefresh = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.36-3.36L23 10M1 14l5.13 4.36A9 9 0 0020.49 15" />;
const IcoClock = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 6v6l4 2" />;
const IcoSpinner = ({ size = 16, style = {} }: { size?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', animation: 'flc-spin 0.75s linear infinite', ...style }}>
    <path d="M12 2a10 10 0 0110 10" />
  </svg>
);

// ── Types ─────────────────────────────────────────────────────────────────────
type ID = string | number;
type LateFeeType = 'per_day' | 'per_month' | 'percentage' | 'none';

interface MonthConfig {
  month: string;       // "April 2025"
  monthYear: string;   // "2025-04"
  dueDate: string;     // "2025-04-15"
  lateFeeType: LateFeeType;
  lateFeeAmount: number;
  lateFeeMax: number | null;
  graceDays: number;
  isDirty?: boolean;
}

interface ClassLateConfig {
  configId: string | null;
  classId: ID;
  className: string;
  months: MonthConfig[];
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const months = MONTH_NAMES;

const MONTH_FROM_NUM: Record<number, string> = {
  1: 'January', 2: 'February', 3: 'March', 4: 'April',
  5: 'May', 6: 'June', 7: 'July', 8: 'August',
  9: 'September', 10: 'October', 11: 'November', 12: 'December',
};

const LATE_FEE_TYPES: { value: LateFeeType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'per_day', label: 'Per Day' },
  { value: 'per_month', label: 'Per Month' },
  { value: 'percentage', label: 'Percentage' },
];

const normalizeSession = (session: string): string => {
  if (!session) return '';
  const parts = session.split('-');
  if (parts.length !== 2) return session;
  const start = parseInt(parts[0], 10);
  let end = parseInt(parts[1], 10);
  if (isNaN(start) || isNaN(end)) return session;
  if (end < 100) end = Math.floor(start / 100) * 100 + end;
  return `${start}-${end}`;
};

const toMonthYear = (monthName: string, year: number | string) => {
  const mi = months.indexOf(monthName) + 1;
  return `${year}-${String(mi).padStart(2, '0')}`;
};

const defaultDueDate = (monthYear: string, day = 10) =>
  `${monthYear}-${String(Math.min(Math.max(day, 1), 28)).padStart(2, '0')}`;

/** Keep day-of-month, remap to each month_year (for uniform mode). */
const dueDateForMonth = (sourceDueDate: string, targetMonthYear: string): string => {
  const day = parseInt((sourceDueDate || '').split('-')[2] || '10', 10);
  return defaultDueDate(targetMonthYear, isNaN(day) ? 10 : day);
};

const generateMonthsForSession = (opts: {
  session: string;
  durationStart: string;
  durationEnd: string;
}): { month: string; year: number }[] => {
  const { session, durationStart, durationEnd } = opts;
  if (!session || !durationStart || !durationEnd) return [];
  const normalized = normalizeSession(session);
  const parts = normalized.split('-').map(y => parseInt(y, 10));
  if (parts.length !== 2 || parts.some(isNaN)) return [];
  const [startYear, endYear] = parts;
  const si = months.indexOf(durationStart);
  const ei = months.indexOf(durationEnd);
  if (si === -1 || ei === -1) return [];

  const result: { month: string; year: number }[] = [];
  let mi = si;
  let yr = startYear;
  while (true) {
    result.push({ month: months[mi], year: yr });
    if (mi === ei && yr === endYear) break;
    mi++;
    if (mi > 11) { mi = 0; yr++; }
    if (result.length > 24) break;
  }
  return result;
};

const emptyMonthConfig = (month: string, year: number): MonthConfig => {
  const my = toMonthYear(month, year);
  return {
    month: `${month} ${year}`,
    monthYear: my,
    dueDate: defaultDueDate(my),
    lateFeeType: 'none',
    lateFeeAmount: 0,
    lateFeeMax: null,
    graceDays: 0,
  };
};

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
  .flc-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }
  @keyframes flc-fadeSlide { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes flc-spin { to { transform: rotate(360deg); } }

  .flc-card {
    background: white; border-radius: 16px; padding: 20px; margin-bottom: 16px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06); border: 2px solid #dbeafe;
    animation: flc-fadeSlide 0.35s ease-out;
  }
  .flc-month-card {
    background: #fafbfc; border: 1.5px solid #e8edf5; border-radius: 12px;
    padding: 16px; margin-bottom: 12px; transition: border-color 0.15s;
  }
  .flc-month-card.dirty { border-color: #f59e0b; background: #fffbeb; }
  .flc-month-card.has-config { border-color: #bbf7d0; background: #f0fdf4; }

  .flc-btn-primary {
    background: linear-gradient(135deg, #1e3a8a, #2563eb);
    color: white; border: none; border-radius: 11px;
    padding: 10px 22px; font-size: 13px; font-weight: 700;
    cursor: pointer; transition: all 0.15s; display: inline-flex; align-items: center; gap: 8px;
    box-shadow: 0 2px 8px rgba(37,99,235,0.25); font-family: 'DM Sans', sans-serif;
  }
  .flc-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(37,99,235,0.35); }
  .flc-btn-primary:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
  .flc-btn-secondary {
    background: white; color: #475569; border: 1.5px solid #e2e8f0;
    border-radius: 11px; padding: 10px 22px; font-size: 13px; font-weight: 700;
    cursor: pointer; transition: all 0.15s; display: inline-flex; align-items: center; gap: 8px;
    font-family: 'DM Sans', sans-serif;
  }
  .flc-btn-secondary:hover { border-color: #93c5fd; color: #1e40af; background: #eff6ff; }
  .flc-btn-danger {
    background: linear-gradient(135deg,#ef4444,#dc2626); color: white; border: none;
    border-radius: 8px; padding: 7px 14px; font-size: 12px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  }
  .flc-btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

  .flc-select {
    border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 8px 10px;
    font-size: 13px; font-weight: 600; outline: none; background: white;
    font-family: 'DM Sans', sans-serif; color: #334155; min-width: 130px;
  }
  .flc-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
  .flc-input {
    border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 8px 10px;
    font-size: 13px; font-weight: 600; outline: none; background: white;
    font-family: 'DM Sans', sans-serif; color: #334155; width: 100%;
  }
  .flc-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
  .flc-label {
    display: block; font-size: 11px; font-weight: 700; color: #64748b;
    margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.3px;
  }
`;

// ── Month field grid (shared by uniform + per-month) ──────────────────────────
function MonthFields({
  m,
  canEdit,
  onChange,
}: {
  m: MonthConfig;
  canEdit: boolean;
  onChange: (patch: Partial<MonthConfig>) => void;
}) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 12,
    }}>
      <div>
        <label className="flc-label">Due Date</label>
        <input
          type="date"
          className="flc-input"
          value={m.dueDate}
          disabled={!canEdit}
          onChange={e => onChange({ dueDate: e.target.value })}
        />
      </div>

      <div>
        <label className="flc-label">Late Fee Type</label>
        <select
          className="flc-select"
          value={m.lateFeeType}
          disabled={!canEdit}
          onChange={e => onChange({
            lateFeeType: e.target.value as LateFeeType,
            lateFeeAmount: e.target.value === 'none' ? 0 : m.lateFeeAmount,
          })}
          style={{ width: '100%' }}
        >
          {LATE_FEE_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="flc-label">
          Amount {m.lateFeeType === 'percentage' ? '(%)' : '(₹)'}
        </label>
        <InputNumber
          value={m.lateFeeAmount}
          onValueChange={e => onChange({ lateFeeAmount: e.value ?? 0 })}
          mode={m.lateFeeType === 'percentage' ? 'decimal' : 'currency'}
          currency="INR"
          locale="en-IN"
          min={0}
          max={m.lateFeeType === 'percentage' ? 100 : undefined}
          disabled={!canEdit || m.lateFeeType === 'none'}
          style={{ width: '100%' }}
          inputStyle={{
            width: '100%', border: '1px solid #d1d5db', borderRadius: 6,
            padding: '8px 10px', fontSize: 13,
            backgroundColor: (!canEdit || m.lateFeeType === 'none') ? '#f3f4f6' : 'white',
          }}
        />
      </div>

      <div>
        <label className="flc-label">Max Cap (₹) — optional</label>
        <InputNumber
          value={m.lateFeeMax}
          onValueChange={e => onChange({ lateFeeMax: e.value ?? null })}
          mode="currency"
          currency="INR"
          locale="en-IN"
          min={0}
          disabled={!canEdit || m.lateFeeType === 'none'}
          style={{ width: '100%' }}
          inputStyle={{
            width: '100%', border: '1px solid #d1d5db', borderRadius: 6,
            padding: '8px 10px', fontSize: 13,
            backgroundColor: (!canEdit || m.lateFeeType === 'none') ? '#f3f4f6' : 'white',
          }}
          placeholder="No max"
        />
      </div>

      <div>
        <label className="flc-label">Grace Days</label>
        <InputNumber
          value={m.graceDays}
          onValueChange={e => onChange({ graceDays: e.value ?? 0 })}
          min={0}
          max={31}
          disabled={!canEdit}
          style={{ width: '100%' }}
          inputStyle={{
            width: '100%', border: '1px solid #d1d5db', borderRadius: 6,
            padding: '8px 10px', fontSize: 13,
            backgroundColor: !canEdit ? '#f3f4f6' : 'white',
          }}
        />
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function FeeLateConfigSetup() {
  const { classes, loading: classesLoading, fetchClasses } = useClassesStore();
  const { schoolDetails, loading: schoolLoading } = useSchoolDetailsStore();

  const schoolId = schoolDetails?.school_id ?? '';
  const currentSession = schoolDetails?.current_session ?? null;
  const sessionId = currentSession?.id ?? '';
  const displaySession = currentSession?.session ?? '';
  const sessionStr = currentSession ? normalizeSession(currentSession.session) : '';
  const durationStart = currentSession?.start_month
    ? (MONTH_FROM_NUM[currentSession.start_month] ?? '')
    : '';
  const durationEnd = currentSession?.end_month
    ? (MONTH_FROM_NUM[currentSession.end_month] ?? '')
    : '';

  const [selectedClassId, setSelectedClassId] = useState<ID | null>(null);
  const [config, setConfig] = useState<ClassLateConfig | null>(null);
  const [sessionMonths, setSessionMonths] = useState<{ month: string; year: number }[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingMonth, setDeletingMonth] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  /** Same rules for all months — mirrors Fee Structure "Uniform Annual Fees" */
  const [uniform, setUniform] = useState(false);

  const toast = useRef<Toast>(null);
  const hasExisting = Boolean(config?.configId);
  const canEdit = !hasExisting || isEditing;

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (sessionStr && durationStart && durationEnd) {
      setSessionMonths(generateMonthsForSession({ session: sessionStr, durationStart, durationEnd }));
    } else {
      setSessionMonths([]);
    }
  }, [sessionStr, durationStart, durationEnd]);

  useEffect(() => {
    if (!selectedClassId && classes?.length && sessionMonths.length && schoolId && sessionId) {
      loadClassConfig(classes[0].id);
    }
  }, [classes, sessionMonths, schoolId, sessionId]);

  // When turning uniform ON, copy first month's rules onto every month
  // (due date keeps day-of-month only).
  const handleUniformToggle = (checked: boolean) => {
    setUniform(checked);
    if (checked && config && config.months.length > 0) {
      const source = config.months[0];
      setConfig(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          months: prev.months.map(m => ({
            ...m,
            dueDate: dueDateForMonth(source.dueDate, m.monthYear),
            lateFeeType: source.lateFeeType,
            lateFeeAmount: source.lateFeeAmount,
            lateFeeMax: source.lateFeeMax,
            graceDays: source.graceDays,
            isDirty: true,
          })),
        };
      });
    }
  };

  const loadClassConfig = async (classId: ID) => {
    if (!schoolId || !sessionId) return;
    setLocalLoading(true);
    setIsEditing(false);
    setUniform(false);
    try {
      const params = new URLSearchParams({
        school_id: schoolId,
        session_id: sessionId,
        class_id: String(classId),
        limit: '50',
      });
      const res = await apiClient.get(`/api/fee-late-config?${params.toString()}`);
      const rows: any[] = res.data?.data ?? [];
      const doc = rows[0] ?? null;

      const cls = (classes || []).find((c: any) => String(c.id) === String(classId));
      const className = doc?.class_name || cls?.class_name || '';

      const existingByMonth: Record<string, any> = {};
      if (doc?.months) {
        for (const m of doc.months) {
          existingByMonth[m.month_year] = m;
        }
      }

      const monthConfigs: MonthConfig[] = sessionMonths.map(sm => {
        const key = toMonthYear(sm.month, sm.year);
        const ex = existingByMonth[key];
        if (ex) {
          return {
            month: `${sm.month} ${sm.year}`,
            monthYear: key,
            dueDate: ex.due_date || defaultDueDate(key),
            lateFeeType: (ex.late_fee_type || 'none') as LateFeeType,
            lateFeeAmount: ex.late_fee_amount ?? 0,
            lateFeeMax: ex.late_fee_max ?? null,
            graceDays: ex.grace_days ?? 0,
          };
        }
        return emptyMonthConfig(sm.month, sm.year);
      });

      setConfig({
        configId: doc?.id || doc?._id || null,
        classId,
        className,
        months: monthConfigs,
      });
      setSelectedClassId(classId);
    } catch (err) {
      console.error('[loadClassConfig]', err);
      const cls = (classes || []).find((c: any) => String(c.id) === String(classId));
      setConfig({
        configId: null,
        classId,
        className: cls?.class_name || '',
        months: sessionMonths.map(sm => emptyMonthConfig(sm.month, sm.year)),
      });
      setSelectedClassId(classId);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleClassSelect = (classId: ID) => {
    if (String(classId) === String(selectedClassId)) return;
    loadClassConfig(classId);
  };

  /**
   * Update one month, or all months when uniform is on.
   * For dueDate in uniform mode: keep the day, remap to each month_year.
   */
  const updateMonth = (index: number, patch: Partial<MonthConfig>) => {
    if (!config || !canEdit) return;
    setConfig(prev => {
      if (!prev) return prev;

      if (uniform) {
        return {
          ...prev,
          months: prev.months.map(m => {
            const next: MonthConfig = {
              ...m,
              ...patch,
              isDirty: true,
            };
            // Due date must stay relative to each month
            if (patch.dueDate !== undefined) {
              next.dueDate = dueDateForMonth(patch.dueDate, m.monthYear);
            }
            // Never overwrite month labels / keys
            next.month = m.month;
            next.monthYear = m.monthYear;
            return next;
          }),
        };
      }

      return {
        ...prev,
        months: prev.months.map((m, i) =>
          i === index ? { ...m, ...patch, isDirty: true } : m
        ),
      };
    });
  };

  const handleSave = async () => {
    if (!config || !schoolId || !sessionId) return;
    setSaving(true);
    try {
      const monthsPayload = config.months.map(m => ({
        month_year: m.monthYear,
        due_date: m.dueDate,
        late_fee_type: m.lateFeeType,
        late_fee_amount: m.lateFeeType === 'none' ? 0 : m.lateFeeAmount,
        late_fee_max: m.lateFeeMax,
        grace_days: m.graceDays,
      }));

      if (config.configId && isEditing) {
        const res = await apiClient.put('/api/fee-late-config/update', {
          config_id: config.configId,
          months: monthsPayload,
        });
        if (res.data.status === 'success') {
          toast.current?.show({
            severity: 'success',
            summary: 'Updated',
            detail: `Late fee config updated for ${config.className}`,
            life: 3000,
          });
          setIsEditing(false);
          setUniform(false);
          await loadClassConfig(config.classId);
        } else {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message, life: 4000 });
        }
      } else {
        const res = await apiClient.post('/api/fee-late-config', {
          school_id: schoolId,
          session_id: sessionId,
          session: displaySession || sessionStr,
          class_id: String(config.classId),
          months: monthsPayload,
        });
        if (res.data.status === 'success') {
          toast.current?.show({
            severity: 'success',
            summary: 'Saved',
            detail: uniform
              ? `Uniform late fee rules applied to all ${config.months.length} months for ${config.className}`
              : `Late fee config saved for ${config.className}`,
            life: 3000,
          });
          setIsEditing(false);
          setUniform(false);
          await loadClassConfig(config.classId);
        } else {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message, life: 4000 });
        }
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Save failed',
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMonth = async (monthYear: string) => {
    if (!config?.configId) return;
    setDeletingMonth(monthYear);
    try {
      const res = await apiClient.delete('/api/fee-late-config/delete', {
        data: { config_id: config.configId, month_years: [monthYear] },
      });
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: `Month ${monthYear} removed`,
          life: 3000,
        });
        await loadClassConfig(config.classId);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Delete failed',
        life: 4000,
      });
    } finally {
      setDeletingMonth(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!config || !sessionId) return;
    setDeleting(true);
    try {
      let res;
      if (config.configId) {
        res = await apiClient.delete('/api/fee-late-config/delete', {
          data: { config_id: config.configId },
        });
      } else {
        res = await apiClient.delete('/api/fee-late-config/bulk-delete', {
          data: { class_id: String(config.classId), session_id: sessionId },
        });
      }
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: `Late fee config deleted for ${config.className}`,
          life: 3000,
        });
        setIsEditing(false);
        setUniform(false);
        await loadClassConfig(config.classId);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Delete failed',
        life: 4000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const isLoadingInitial = classesLoading || schoolLoading;
  const contextReady = Boolean(schoolId && sessionId);

  return (
    <div className="flc-root" style={{ minHeight: '100%', padding: 0, background: 'transparent' }}>
      <style>{styles}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="flc-card" style={{
        background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
        border: 'none', color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 12, padding: 10, display: 'flex' }}>
            <IcoClock size={20} style={{ color: 'white' }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'white' }}>Late Fee Configuration</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
              Set due dates, grace days & late fee rules per class / month
            </p>
          </div>
        </div>
        {displaySession && (
          <span style={{
            background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: 12,
            fontSize: 12, fontWeight: 600, color: 'white',
          }}>
            Session: {displaySession}
          </span>
        )}
      </div>

      {!isLoadingInitial && !contextReady && (
        <div className="flc-card" style={{ textAlign: 'center', padding: 40 }}>
          <IcoWarn size={32} style={{ color: '#f59e0b', display: 'block', margin: '0 auto 12px' }} />
          <p style={{ color: '#92400e', fontSize: 14, fontWeight: 600 }}>
            School or current session not loaded.
          </p>
        </div>
      )}

      {isLoadingInitial && (
        <div className="flc-card" style={{ textAlign: 'center', padding: 40 }}>
          <IcoSpinner size={40} style={{ color: '#2855ae', display: 'block', margin: '0 auto' }} />
          <p style={{ color: '#6b7280', marginTop: 14, fontSize: 14 }}>Loading...</p>
        </div>
      )}

      {!isLoadingInitial && contextReady && classes?.length > 0 && (
        <>
          {/* Class selector */}
          <div className="flc-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{
                background: 'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius: 10,
                padding: 8, minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <IcoGradCap size={14} style={{ color: 'white' }} />
              </div>
              <h4 style={{ color: '#2855ae', fontSize: 14, fontWeight: 600, margin: 0 }}>Select Class</h4>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {classes.map((cls: any) => (
                <Chip
                  key={String(cls.id)}
                  label={cls.class_name}
                  onClick={() => handleClassSelect(cls.id)}
                  style={{
                    background: String(selectedClassId) === String(cls.id) ? '#e0f2fe' : '#f3f4f6',
                    color: String(selectedClassId) === String(cls.id) ? '#0369a1' : '#4b5563',
                    fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 12,
                    border: String(selectedClassId) === String(cls.id) ? '2px solid #2855ae' : '1px solid #e5e7eb',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>
          </div>

          {localLoading ? (
            <div className="flc-card" style={{ textAlign: 'center', padding: 48 }}>
              <IcoSpinner size={40} style={{ color: '#2855ae', display: 'block', margin: '0 auto' }} />
              <p style={{ color: '#6b7280', marginTop: 14 }}>Loading late fee config...</p>
            </div>
          ) : config ? (
            <>
              {/* Status banner */}
              <div style={{
                background: hasExisting ? (isEditing ? '#fef3c7' : '#eff6ff') : '#fef3c7',
                border: `1px solid ${hasExisting ? (isEditing ? '#fbbf24' : '#93c5fd') : '#fbbf24'}`,
                borderRadius: 12, padding: '12px 16px', marginBottom: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {hasExisting && !isEditing
                    ? <IcoInfo size={16} style={{ color: '#3b82f6' }} />
                    : <IcoWarn size={16} style={{ color: '#f59e0b' }} />}
                  <span style={{
                    color: hasExisting && !isEditing ? '#1e40af' : '#92400e',
                    fontSize: 13, fontWeight: 500,
                  }}>
                    {hasExisting
                      ? (isEditing ? 'Editing mode — update due dates & late fees then save.' : 'Late fee config loaded for this class.')
                      : 'No late fee config yet. Configure months below and save.'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {hasExisting && !isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      style={{
                        background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none',
                        borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6, color: 'white',
                        fontWeight: 600, fontSize: 12,
                      }}
                    >
                      <IcoPencil size={13} style={{ color: 'white' }} /> Edit
                    </button>
                  )}
                  {isEditing && (
                    <>
                      <button
                        onClick={() => { setIsEditing(false); setUniform(false); loadClassConfig(config.classId); }}
                        className="flc-btn-secondary"
                        style={{ padding: '7px 14px', fontSize: 12 }}
                      >
                        <IcoX size={13} /> Cancel
                      </button>
                      <button className="flc-btn-danger" onClick={handleDeleteAll} disabled={deleting}>
                        {deleting ? <IcoSpinner size={13} /> : <IcoTrash size={13} style={{ color: 'white' }} />}
                        {deleting ? 'Deleting...' : 'Delete All'}
                      </button>
                    </>
                  )}
                  {!hasExisting && (
                    <button
                      onClick={() => loadClassConfig(config.classId)}
                      className="flc-btn-secondary"
                      style={{ padding: '7px 14px', fontSize: 12 }}
                    >
                      <IcoRefresh size={13} /> Reload
                    </button>
                  )}
                </div>
              </div>

              {/* Uniform toggle — same pattern as Fee Structure */}
              <div className="flc-card" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <input
                  type="checkbox"
                  id="uniformLate"
                  checked={uniform}
                  onChange={e => handleUniformToggle(e.target.checked)}
                  disabled={!canEdit}
                  style={{
                    width: 18, height: 18,
                    cursor: canEdit ? 'pointer' : 'not-allowed',
                    accentColor: '#2855ae',
                    opacity: canEdit ? 1 : 0.5,
                  }}
                />
                <label
                  htmlFor="uniformLate"
                  style={{
                    color: canEdit ? '#374151' : '#9ca3af',
                    fontSize: 14, fontWeight: 600,
                    cursor: canEdit ? 'pointer' : 'not-allowed',
                    userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <IcoClone size={14} style={{ color: canEdit ? '#3b82f6' : '#9ca3af' }} />
                  Same late fee rules for all months (Uniform Annual Config)
                </label>
                {uniform && canEdit && (
                  <span style={{
                    marginLeft: 'auto', background: '#dcfce7', color: '#166534',
                    padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  }}>
                    Active
                  </span>
                )}
              </div>

              {/* ── Uniform view: single card applied to all months ── */}
              {uniform ? (
                <div className="flc-card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{
                      background: 'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius: 10,
                      padding: 8, minWidth: 32, height: 32,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <IcoCalendar size={14} style={{ color: 'white' }} />
                    </div>
                    <h3 style={{ color: '#2855ae', fontSize: 16, fontWeight: 600, margin: 0 }}>
                      Standard Late Fee Rules (Applied to All Months)
                    </h3>
                    <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 13, fontWeight: 600 }}>
                      {config.months.length} months
                    </span>
                  </div>

                  {config.months[0] && (
                    <MonthFields
                      m={config.months[0]}
                      canEdit={canEdit}
                      onChange={patch => updateMonth(0, patch)}
                    />
                  )}

                  <div style={{
                    marginTop: 16, background: '#eff6ff', border: '1px solid #93c5fd',
                    borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <IcoInfo size={16} style={{ color: '#3b82f6' }} />
                    <span style={{ color: '#1e40af', fontSize: 13 }}>
                      These rules will be applied uniformly to all <strong>{config.months.length}</strong> months
                      of the academic session. The due-date <em>day</em> is kept the same for every month
                      (e.g. the 10th of each month).
                    </span>
                  </div>
                </div>
              ) : (
                /* ── Per-month cards ── */
                config.months.map((m, idx) => {
                  const hasData = m.lateFeeType !== 'none' || m.graceDays > 0;
                  return (
                    <div
                      key={m.monthYear}
                      className={`flc-month-card${m.isDirty ? ' dirty' : ''}${hasData && hasExisting ? ' has-config' : ''}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                        <div style={{
                          background: 'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius: 10,
                          padding: 8, minWidth: 32, height: 32,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <span style={{ fontSize: 13, color: 'white', fontWeight: 700 }}>{idx + 1}</span>
                        </div>
                        <h3 style={{ color: '#2855ae', fontSize: 15, fontWeight: 600, margin: 0 }}>
                          {m.month}
                        </h3>
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                          {m.monthYear}
                        </span>
                        {isEditing && hasExisting && (
                          <button
                            onClick={() => handleDeleteMonth(m.monthYear)}
                            disabled={deletingMonth === m.monthYear}
                            style={{
                              background: '#fee2e2', border: 'none', borderRadius: 7,
                              padding: '5px 10px', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 5,
                              color: '#dc2626', fontWeight: 600, fontSize: 11,
                              opacity: deletingMonth === m.monthYear ? 0.6 : 1,
                            }}
                          >
                            {deletingMonth === m.monthYear
                              ? <IcoSpinner size={11} />
                              : <IcoTrash size={11} style={{ color: '#dc2626' }} />}
                            {deletingMonth === m.monthYear ? '...' : 'Delete'}
                          </button>
                        )}
                      </div>

                      <MonthFields
                        m={m}
                        canEdit={canEdit}
                        onChange={patch => updateMonth(idx, patch)}
                      />
                    </div>
                  );
                })
              )}

              {/* Save bar */}
              <div className="flc-card" style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: 12,
              }}>
                <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                  {config.months.length} month{config.months.length !== 1 ? 's' : ''} · {config.className}
                  {uniform && (
                    <span style={{
                      marginLeft: 10, background: '#dcfce7', color: '#166534',
                      padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                    }}>
                      Uniform
                    </span>
                  )}
                </p>
                <button
                  className="flc-btn-primary"
                  onClick={handleSave}
                  disabled={saving || (hasExisting && !isEditing)}
                >
                  {saving ? <IcoSpinner size={14} /> : <IcoCheck size={14} />}
                  {saving
                    ? 'Saving...'
                    : hasExisting
                    ? 'Save Changes'
                    : uniform
                    ? 'Save Uniform Config'
                    : 'Save Late Fee Config'}
                </button>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}